import classNames from 'classnames';
import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import * as React from 'react';
import { ReactElement } from 'react';

import styles from './Tabs.module.css';

import NextImage from '@/shared/components/atoms/NextImage';
import { ITabsProps } from '@/shared/components/atoms/Tabs/ITabs';
import { Typography } from '@/shared/components/atoms/Typography';
import { clsxm } from '@/shared/lib';

export default function Tabs<T>({
  getCount,
  getLabel,
  getValue,
  getImage,
  tabsList,
  tabQueryIndicator,
  tabClasseName,
  activeClassName,
  linkClassName,
  containerClassName,
  imageWrapperClassName,
  variant,
  defaultValue,
  allInfo,
  resetQueriesOnTabChange,
}: ITabsProps<T>): ReactElement {
  const { query, replace, isReady } = useRouter();
  const { i18n } = useTranslation('common');
  /************************** styles conditions *************************** */
  const containerStyle = clsx(
    'scrollbar-thin scrollbar-thumb-background-400 scrollbar-rounded-lg scrollbar-h-1 ',
    styles['container'],
    containerClassName || '',
    variant !== 'connected' && 'gap-2'
  );

  const tabStyleAllInfo = classNames(
    tabClasseName,
    variant !== 'headless' && styles[variant],
    //  INFO: fix styling of border radius for connected variant on different directions
    styles[i18n.dir()],
    query?.[tabQueryIndicator] === allInfo?.value?.toString() &&
      (activeClassName || styles[`active-${variant}`])
  );
  const linkStyle = clsxm(
    'flex w-full items-center gap-1',
    variant === 'rounded' ? 'px-20 py-5' : 'px-24 py-2',
    linkClassName
  );

  // set tab default value
  React.useEffect(() => {
    if (
      isReady &&
      !query?.[tabQueryIndicator] &&
      (defaultValue || allInfo?.value)
    ) {
      replace(
        {
          query: {
            ...(!resetQueriesOnTabChange ? query : null),
            [tabQueryIndicator]: defaultValue ? defaultValue : allInfo?.value,
          },
        },
        undefined
        // { shallow: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsList]);

  return (
    <ul className={containerStyle}>
      {/* if tabs start with all */}
      {allInfo && (
        <li className={tabStyleAllInfo}>
          <Link
            prefetch={false}
            className={linkStyle}
            shallow
            href={{
              query: {
                ...(!resetQueriesOnTabChange ? query : null),
                [tabQueryIndicator]: allInfo?.value,
              },
            }}
          >
            <Typography
              variant='subtitle1'
              weight='regular'
              className='text-clip whitespace-nowrap break-keep'
            >
              {allInfo?.name}
            </Typography>
            {allInfo?.count !== undefined && (
              <Typography> ({allInfo?.count})</Typography>
            )}
          </Link>
        </li>
      )}
      {tabsList.map(
        (tab) =>
          getValue(tab) && (
            <li
              key={getValue(tab)}
              className={clsx(
                tabClasseName,
                variant !== 'headless' && styles[variant],
                styles[i18n.dir()],
                query?.[tabQueryIndicator] === getValue(tab)?.toString() &&
                  (activeClassName || styles[`active-${variant}`])
              )}
            >
              <Link
                prefetch={false}
                className={linkStyle}
                shallow
                href={{
                  query: {
                    ...(!resetQueriesOnTabChange ? query : null),
                    [tabQueryIndicator]: getValue(tab),
                  },
                }}
              >
                {getImage && (
                  <div
                    className={imageWrapperClassName ?? styles['imageWrapper']}
                  >
                    <NextImage
                      imgClassName={styles['image']}
                      width={46}
                      height={46}
                      src={getImage(tab) || ''}
                      alt={getLabel(tab)}
                    />
                  </div>
                )}
                <Typography
                  variant='subtitle2'
                  weight='regular'
                  className='text-clip whitespace-nowrap break-keep capitalize'
                >
                  {getLabel(tab)}
                </Typography>
                {getCount && <Typography> ({getCount(tab)})</Typography>}
              </Link>
            </li>
          )
      )}
    </ul>
  );
}
